import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';
import i18n from '../../i18n';
import * as chatwoot from '../../services/chatwoot';
import { resetMediaUploadSession } from '../../utils/mediaUploadClient';
import { setNumberDigits } from '../../utils/locale';

const API_URL = '/api/auth';
// Set when a branch role (per-branch roles) forced the staff view; see loadUser.
const BRANCH_STAFF_VIEW = 'mm.branchStaffView';

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/login`, credentials);
    localStorage.setItem('token', response.data.token);
    return response.data;
  } catch (error) {
    return rejectWithValue(rejectBody(error, 'Login failed'));
  }
});

/**
 * A rejected OTP call, as an object rather than a bare string.
 *
 * The lockout needs to travel: the screen has to know how long to count down and
 * how many tries are left, not just that something failed. `message` stays on it
 * so anything that renders the error directly still shows text. Login.js runs
 * every shape through errorText() — it must, because EVERY thunk here now
 * rejects with the response body, and rendering that object raw is a React
 * crash on the one screen nobody can get past.
 */
const otpRejection = (error, fallback) => {
  const data = error.response?.data || {};
  return {
    message: data.message || data.error || fallback,
    code: data.error,
    // 423 = the account is locked. Seconds, from the server, so a client clock
    // that is wrong or a tab that sat idle cannot shorten the wait.
    retryAfterSeconds: Number(data.retryAfterSeconds) || 0,
    attemptsRemaining:
      typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : null,
  };
};

export const sendOtp = createAsyncThunk('auth/sendOtp', async (arg, { rejectWithValue }) => {
  // `channel: 'sms'` is the login screen's "send SMS instead" button, and it is
  // the only value the server reads: the flag is a downgrade, so nothing sent
  // from here can talk it into a WhatsApp send it did not already choose.
  const { phone, channel } = typeof arg === 'string' ? { phone: arg } : arg;
  try {
    // The login code follows the language of the screen in front of the person,
    // not the preference on their user row. At this point in the flow they are
    // not signed in, so the interface they chose is the only signal we have —
    // and users.notification_language defaults to 'ar' with nobody having set
    // it, which is why an English UI was still sending an Arabic code. Every
    // other notification keeps using the stored preference; this is the one
    // message sent to someone who has not identified themselves yet.
    const response = await axios.post(`${API_URL}/otp/send`, {
      phone,
      language: String(i18n.language || '').startsWith('ar') ? 'ar' : 'en',
      ...(channel ? { channel } : {}),
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(otpRejection(error, 'Failed to send OTP'));
  }
});

export const verifyOtp = createAsyncThunk('auth/verifyOtp', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/otp/verify`, data);
    localStorage.setItem('token', response.data.token);
    return response.data;
  } catch (error) {
    return rejectWithValue(otpRejection(error, 'Failed to verify OTP'));
  }
});

export const loadUser = createAsyncThunk('auth/loadUser', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return rejectWithValue('No token found');
    }
    const response = await axios.get(`${API_URL}/me`);
    // Mirror the per-company digit-style setting into the locale helper here,
    // not via a second GET at boot in App.js. The auth/me call already runs
    // for every session restore, and the same boot window is the right place
    // to push tenant settings into a module-level mirror: a Settings save
    // updates the mirror, but a re-login re-runs this thunk and resets it.
    // Best-effort: a missing /api/garages/current (e.g. no garage yet) keeps
    // the helper's Latin default — the spec's "missing/unknown → latin" rule.
    try {
      const garageRes = await axios.get('/api/garages/current');
      // GET /api/garages/current serializes the Garage with its included
      // Company row (garageSettingsController.getCurrentGarage) — the setting
      // lives on the company's settings blob, companies.settings.number_digits,
      // not flattened onto the company. setNumberDigits itself falls back to
      // Latin for missing/unknown, so an old payload without settings still
      // boots Latin.
      const value = garageRes?.data?.data?.company?.settings?.number_digits;
      setNumberDigits(value);
    } catch {
      // Keep the helper's existing value; Latin is the documented default.
    }
    return response.data;
  } catch (error) {
    const preserveSession =
      error?.response?.status === 403 &&
      error?.response?.data?.code === 'BRANCH_SCOPE_ALL_MIXED_ROLES';
    if (!preserveSession) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
    return rejectWithValue(
      rejectBody(error, 'Failed to load user'),
      preserveSession ? { preserveSession: true } : undefined
    );
  }
});

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    // Drop the support-chat session with the auth session. Done here rather than
    // in a component cleanup because logout unmounts the whole private tree at
    // once — an unmount handler is not guaranteed to have run before the next
    // person signs in on this browser, and they would inherit the conversation.
    chatwoot.reset();
    // Same for the media-upload session: abort in-flight stages, revoke preview
    // URLs and forget the intake capability cache, so nothing signed-in — no
    // upload, no cached answer — survives for the next identity on this browser.
    resetMediaUploadSession();
    return null;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

/**
 * Force Staff users to staff view mode — on login, OTP login and /auth/me.
 *
 * Per-branch roles: the login bodies and /auth/me send role_by_branch (switch
 * on, user with overrides). Remember that the role worn NOW forced the staff
 * view, so a branch where the user is not Staff gives the admin view back
 * instead of keeping the mechanic screens — also straight after sign-in, when
 * the login body's role (the default) is Staff and the picked branch's role,
 * read by the next /auth/me, is not. Without role_by_branch nothing changes.
 */
function applyRoleViewMode(state, user) {
  if (user.role?.name === 'Staff') {
    state.viewMode = 'staff';
    localStorage.setItem('viewMode', 'staff');
    if (user.role_by_branch) {
      localStorage.setItem(BRANCH_STAFF_VIEW, '1');
    }
  } else if (localStorage.getItem(BRANCH_STAFF_VIEW) === '1') {
    localStorage.removeItem(BRANCH_STAFF_VIEW);
    state.viewMode = 'admin';
    localStorage.setItem('viewMode', 'admin');
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    loading: true,
    // "the app is still working out who you are", which is NOT the same as
    // `loading` above. `loading` is true during ANY auth call, including a
    // login submit — and App.js used it to decide whether to replace the whole
    // Router with a full-screen spinner. Submitting an OTP therefore UNMOUNTED
    // the login screen and remounted it fresh when the request settled: back to
    // the phone step, typed number gone, with only the Redux error left on
    // screen to explain it. This flag is set once at startup and cleared when
    // the initial loadUser settles, so it covers the bootstrap and nothing else.
    initializing: true,
    error: null,
    availableGarages: [],
    activeGarageId: localStorage.getItem('activeGarageId'),
    viewMode: localStorage.getItem('viewMode') || 'admin', // 'admin' or 'staff'
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setActiveGarageId: (state, action) => {
      state.activeGarageId = action.payload;
      localStorage.setItem('activeGarageId', action.payload);
      // A tenant/branch switch ends the media-upload session: in-flight stages
      // are aborted and the intake capability cache is dropped, so the next
      // upload probes under the NEW branch's own authorization rather than
      // inheriting the previous tenant's answer.
      resetMediaUploadSession();
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
      localStorage.setItem('viewMode', action.payload);
      // A choice the user made themselves is never undone by a branch switch.
      localStorage.removeItem(BRANCH_STAFF_VIEW);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.token = action.payload.token;
        state.availableGarages = action.payload.availableGarages || [];

        applyRoleViewMode(state, action.payload.user);

        // Persist active garage if assigned to user
        if (action.payload.user.garage) {
          state.activeGarageId = action.payload.user.garage.id;
          localStorage.setItem('activeGarageId', state.activeGarageId);
        }

        // P8: ask SignInBranchGate to offer the branch pick after this login.
        // Every successful login arms it, garage or not — a company-level user
        // with branch grants but no home branch must get the pick too (the gate
        // itself skips users with at most one branch). Removed on logout / 401 /
        // gate dismissal, so a page refresh (no login) never shows the dialog.
        try {
          sessionStorage.setItem('mm.pickBranchOnLogin', '1');
        } catch (e) {
          // Storage unavailable
        }
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.availableGarages = action.payload.availableGarages || [];

        applyRoleViewMode(state, action.payload.user);

        // Persist active garage if assigned to user
        if (action.payload.user.garage) {
          state.activeGarageId = action.payload.user.garage.id;
          localStorage.setItem('activeGarageId', state.activeGarageId);
        }

        // P8: see the login.fulfilled case above.
        try {
          sessionStorage.setItem('mm.pickBranchOnLogin', '1');
        } catch (e) {
          // Storage unavailable
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loadUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.loading = false;
        state.initializing = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;

        applyRoleViewMode(state, action.payload.user);

        // For Super Admin, we might need to populate availableGarages again if missing
        // or just rely on the login/select flow.
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.loading = false;
        // Not signed in is a settled answer, not a failure to boot. Leaving
        // this true would hold the spinner over the login screen forever.
        state.initializing = false;
        if (action.meta?.preserveSession) return;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.activeGarageId = null;
        localStorage.removeItem('activeGarageId');
        sessionStorage.removeItem('mm.pickBranchOnLogin');
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.activeGarageId = null;
        state.viewMode = 'admin';
        localStorage.removeItem('activeGarageId');
        localStorage.removeItem('viewMode');
        localStorage.removeItem(BRANCH_STAFF_VIEW);
        sessionStorage.removeItem('mm.pickBranchOnLogin');
      });
  },
});

export const { clearError, setActiveGarageId, setViewMode } = authSlice.actions;
export default authSlice.reducer;
